import {
  getArchivedGoalIds,
  getAutoCompleteSuggestions,
  getCompletedGoalIds,
} from '@griply/utils';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import { GoalItem, GoalTreeItem, GoalTreeItemIds, LifeAreaItem } from './types';

type UseFilteredGoalTreeItemsProps = {
  allGoals: Goal[];
  lifeAreas: ReturnType<typeof useSortedLocalizedLifeAreas>;
  searchValue: string;
  expandedItems: Set<ID>;
  goalIndexMap: Map<ID, number>;
};

export const useFilteredGoalTreeItems = ({
  allGoals,
  lifeAreas,
  searchValue,
  expandedItems,
  goalIndexMap,
}: UseFilteredGoalTreeItemsProps): GoalTreeItem[] => {
  const { t } = useTranslation();
  // We need goals map for color resolution
  const goalsMap = useMemo(() => {
    const map = new Map<ID, Goal>();
    allGoals.forEach((g) => map.set(g.id, g));
    return map;
  }, [allGoals]);

  return useMemo(() => {
    const archivedIds = getArchivedGoalIds(allGoals);

    // A completed goal and its whole sub-tree are never valid move targets.
    const completedIds = getCompletedGoalIds(allGoals);

    // 1. Search Mode: Return flattened results
    if (searchValue) {
      const matchedGoals = getAutoCompleteSuggestions(searchValue, allGoals, [
        'name',
      ]).filter((g) => !archivedIds.has(g.id) && !completedIds.has(g.id));
      const matchedLifeAreas = getAutoCompleteSuggestions(
        searchValue,
        lifeAreas,
        ['name'],
      );

      const lifeAreaItems: LifeAreaItem[] = matchedLifeAreas.map((la) => ({
        type: GoalTreeItemIds.LifeArea,
        ...la,
        indentationLevel: 0,
      }));

      const goalItems: GoalItem[] = matchedGoals.map((g) => {
        const parentId = g.parentIds?.length
          ? g.parentIds[g.parentIds.length - 1]
          : undefined;
        const parentName = parentId ? goalsMap.get(parentId)?.name : undefined;
        const displayColor = getLinkedItemColor(g.id, {
          goals: allGoals,
          lifeAreas,
        });

        return {
          type: GoalTreeItemIds.Goal,
          ...g,
          indentationLevel: 0,
          parentName,
          displayColor,
        };
      });

      // NoLifeArea is usually not searchable by name unless we add it explicitly,
      // generally we don't return it in search results in this app context unless it matches.
      // But preserving existing behavior: existing behavior didn't search NoLifeArea.

      return [...lifeAreaItems, ...goalItems];
    }

    // 2. Tree Mode: Filter & Build Tree
    const activeGoalIds = new Set<ID>();
    const neededParentIds = new Set<ID>();

    // Identify active and needed goals (Linear scan)
    allGoals.forEach((goal) => {
      if (archivedIds.has(goal.id) || completedIds.has(goal.id)) {
        return;
      }
      activeGoalIds.add(goal.id);
      goal.parentIds?.forEach((parentId: ID) => neededParentIds.add(parentId));
    });

    const validGoals = allGoals.filter((g) => {
      if (archivedIds.has(g.id) || completedIds.has(g.id)) {
        return false;
      }
      return activeGoalIds.has(g.id) || neededParentIds.has(g.id);
    });

    // Maps for fast lookups
    const validGoalMap = new Map<ID, Goal>();
    const childrenMap = new Map<ID, Goal[]>();

    validGoals.forEach((g) => {
      validGoalMap.set(g.id, g);
      if (!childrenMap.has(g.id)) {
        childrenMap.set(g.id, []);
      }
    });

    // Populate children
    validGoals.forEach((g) => {
      if (!g.parentIds?.length) {
        return;
      }
      const parentId = g.parentIds[g.parentIds.length - 1];
      if (validGoalMap.has(parentId)) {
        childrenMap.get(parentId)?.push(g);
      }
    });

    // Sort children
    const compareGoals = (a: Goal, b: Goal) => {
      const indexA = goalIndexMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const indexB = goalIndexMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      if (indexA !== indexB) {
        return indexA - indexB;
      }
      return (a.name || '').localeCompare(b.name || '');
    };

    childrenMap.forEach((children) => children.sort(compareGoals));

    // Helper: Recursively flatten tree
    const flattenGoalTree = (goal: Goal, level: number): GoalTreeItem[] => {
      const children = childrenMap.get(goal.id) || [];
      const hasChildren = children.length > 0;
      const isExpanded = expandedItems.has(goal.id);

      const displayColor = getLinkedItemColor(goal.id, {
        goals: allGoals,
        lifeAreas,
      });

      const items: GoalTreeItem[] = [
        {
          type: GoalTreeItemIds.Goal,
          hasChildren,
          indentationLevel: level,
          displayColor,
          ...goal,
        },
      ];

      if (isExpanded && hasChildren) {
        children.forEach((child) => {
          items.push(...flattenGoalTree(child, level + 1));
        });
      }

      return items;
    };

    const items: GoalTreeItem[] = [];
    const processedGoalIds = new Set<ID>();

    const goalParentMap = new Map<ID, Goal>();
    allGoals.forEach((g) => goalParentMap.set(g.id, g));

    // For standard tree mode, indentation:
    // Life Area: 0
    // First Level Goal: 1
    // Nested: +1

    const getLifeAreaId = (goalId: ID): ID | undefined => {
      const g = goalParentMap.get(goalId);
      if (!g) {
        return undefined;
      }
      if (g.lifeAreaId) {
        return g.lifeAreaId;
      }
      // If we are at a root goal (no parents), it has no LA if lifeAreaId is missing.
      if (!g.parentIds?.length) {
        return undefined;
      }
      // Recurse up
      return getLifeAreaId(g.parentIds[0]);
    };

    // 3. Process Life Areas
    lifeAreas.forEach((lifeArea) => {
      // Find roots for this life area
      const roots = validGoals.filter((g) => {
        const laId = getLifeAreaId(g.id);
        if (laId !== lifeArea.id) {
          return false;
        }
        // It's a root if it has no parents OR its parent is not in our valid set
        if (!g.parentIds?.length) {
          return true;
        }
        const parentId = g.parentIds[g.parentIds.length - 1];
        return !validGoalMap.has(parentId);
      });

      const hasChildren = roots.length > 0;
      items.push({
        type: GoalTreeItemIds.LifeArea,
        hasChildren,
        indentationLevel: 0,
        ...lifeArea,
      });

      if (!expandedItems.has(lifeArea.id)) {
        return;
      }

      roots.sort(compareGoals);

      roots.forEach((root) => {
        // Roots under Life Area start at level 1
        const flatTree = flattenGoalTree(root, 1);
        flatTree.forEach((item) => {
          items.push(item);
          if (item.type === GoalTreeItemIds.Goal) {
            processedGoalIds.add(item.id);
          }
        });
      });
    });

    // 4. Process "No Life Area"
    const noLifeAreaRoots = validGoals.filter((g) => {
      if (processedGoalIds.has(g.id)) {
        return false;
      }
      const laId = getLifeAreaId(g.id);
      if (laId) {
        return false;
      }
      if (!g.parentIds?.length) {
        return true;
      }
      const parentId = g.parentIds[g.parentIds.length - 1];
      return !validGoalMap.has(parentId);
    });

    const hasNoLifeAreaChildren = noLifeAreaRoots.length > 0;
    items.push({
      type: GoalTreeItemIds.NoLifeArea,
      id: GoalTreeItemIds.NoLifeArea,
      name: t('general.labels.life-area.no_life_area'),
      hasChildren: hasNoLifeAreaChildren,
      indentationLevel: 0,
    });

    if (expandedItems.has(GoalTreeItemIds.NoLifeArea)) {
      noLifeAreaRoots.sort(compareGoals);
      noLifeAreaRoots.forEach((root) => {
        // No Life Area roots also start at level 1 (visual hierarchy consistent with LA roots)
        items.push(...flattenGoalTree(root, 1));
      });
    }

    return items;
  }, [
    searchValue,
    allGoals,
    lifeAreas,
    goalIndexMap,
    expandedItems,
    goalsMap,
    t,
  ]);
};
